import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClickEventComponentNames, CustomEventNames } from 'features/analytics';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { IStore } from 'features/store/pages/pickup-store-selector/ducks/stores';
import { useMapFocus } from 'features/store/pages/pickup-store-selector/hooks/use-map-focus';
import { StyleSheet } from 'react-native';

import { Box } from 'components/ui';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useMap } from 'hooks/use-map-extended';
import { useMarkerGenerator } from 'hooks/use-map-extended/use-marker-generator';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';

import { FocusedBy, MapViewRef, StoreLocatorActionType } from '../types';

import { MapButtons } from './map-buttons';
import { IMapViewProps } from './types';

import { StoreLocatorMap } from './index';

export const MapView = forwardRef<MapViewRef, IMapViewProps>(
  (
    {
      onCenterToUserLocation,
      onPressSearchThisArea,
      onMarkerPress,
      restaurantFocused,
      actions,
      activeStore,
      closestStore,
      stores,
      storesFavs,
      searchingNearby,
      isStoreEligible,
    },
    ref
  ) => {
    const isDesktop = useIsDesktopBreakpoint();

    const {
      isPermissionGranted,
      manualAddress,
      resetCoordinatesManual,
      setSearchThisAreaCoordinates,
      getCoordinatesByPriority,
      manualCoordinates,
      userCoordinates,
    } = useGeolocation();

    const [hasPanned, setHasPanned] = useState(false);
    // This is needed bc we control the animation using timeouts, so if the user closes
    // the component gets destroyed before the animation end we are going to have a crash on the app
    const animationsTimeouts = useRef<number[]>([]);

    const {
      mapComponent,
      focusToPoint,
      createMarkerList,
      renderMarkers,
      focusToPoints,
      getCurrentCenter,
    } = useMap({
      onDragEnd: () => {
        setHasPanned(true);
      },
    });

    const handleOnMarkerPress = useCallback(
      (store: IStore) => {
        if (!store._id || !onMarkerPress) {
          return;
        }

        actions.current[StoreLocatorActionType.USER_SELECTION] = true;
        onMarkerPress({ restaurant: store, focusedBy: FocusedBy.MAP });
        setHasPanned(false);

        if (activeStore?._id !== store._id) {
          logButtonClick({
            attributes: {
              ...(store._id && { StoreId: store._id }),
              Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              component: ClickEventComponentNames.STORE_LOCATOR_MAP_MARKER,
            },
          });
        }
      },
      [actions, activeStore?._id, onMarkerPress]
    );

    const generateMarkersData = useMarkerGenerator({
      // this is to match the favorites stores with the ones that we have joined on stores
      favoritesStores: storesFavs,
      onPress: handleOnMarkerPress,
      selectedStoreId: activeStore?._id,
      restaurantFocused,
      stores,
      isStoreEligible,
    });

    const handleOnCenterToUserLocation = useCallback(() => {
      onCenterToUserLocation();
      logButtonClick({
        attributes: {
          Name: CustomEventNames.LOCATE_ME,
          name: CustomEventNames.LOCATE_ME,
          component: ClickEventComponentNames.BUTTON,
        },
      });
    }, [onCenterToUserLocation]);

    const handleOnSearchThisArea = useCallback(() => {
      const currentCenter = getCurrentCenter();
      onPressSearchThisArea(currentCenter);
      setHasPanned(false);
    }, [getCurrentCenter, onPressSearchThisArea]);

    const markers = useMemo(() => {
      if (!stores.length) {
        return [];
      }
      return createMarkerList(generateMarkersData());
    }, [createMarkerList, generateMarkersData, stores.length]);

    useEffectOnUnmount(() => {
      if (isPermissionGranted || (manualCoordinates && !manualAddress)) {
        resetCoordinatesManual();
      }
      // This is needed bc we control the animation using timeouts, so if the user closes
      // the component gets destroyed before the animation end we are going to have a crash on the app
      // so we clear all the pending timeouts
      animationsTimeouts.current.forEach(timerId => window.clearTimeout(timerId));
      setSearchThisAreaCoordinates(null);
    });

    useEffect(() => {
      if (Array.isArray(markers) && markers.length) {
        renderMarkers(markers);
      }
    }, [renderMarkers, markers]);

    useMapFocus(ref, {
      mapComponent,
      activeStore,
      restaurantFocused,
      closestStore,
      actions,
      userCoordinates,
      focusToPoint,
      focusToPoints,
      getCoordinatesByPriority,
    });

    return (
      <Box style={styles.mapContainer}>
        <StoreLocatorMap map={mapComponent} />
        <Box style={styles.mapButtonsOverlay}>
          <MapButtons
            hasPanned={hasPanned}
            isDesktop={isDesktop}
            onCenterToUserLocation={handleOnCenterToUserLocation}
            onPressSearchThisArea={handleOnSearchThisArea}
            searchingNearby={searchingNearby}
          />
        </Box>
      </Box>
    );
  }
);

const styles = StyleSheet.create({
  mapButtonsOverlay: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  mapContainer: {
    flex: 1,
  },
});
